<template>
  <div>
    <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div class="flex justify-between items-center mb-2">
        <span class="text-sm font-medium text-gray-600 dark:text-gray-400">
          Subtotal
        </span>
        <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
          ${{ displayedSubtotal.toFixed(2) }}
        </span>
      </div>

      <div
        v-if="currentDiscount && currentDiscount > 0"
        class="mb-2"
      >
        <div
          v-if="isCalculatingPromotions"
          class="flex justify-between items-center mb-2"
        >
          <span class="text-sm font-medium text-purple-600 dark:text-purple-400">
            Calculando promociones...
          </span>
          <span class="text-sm font-medium text-purple-900 dark:text-purple-100">
            <div class="animate-pulse">⏳</div>
          </span>
        </div>

        <div v-else>
          <div
            v-if="appliedPromotions && appliedPromotions.length > 0"
            class="mb-2 space-y-1"
          >
            <div
              v-for="promotion in appliedPromotions"
              :key="promotion.promotion_id" 
              class="flex justify-between items-center bg-green-50 dark:bg-green-900/20 rounded px-2 py-1"
            >
              <span class="text-xs font-medium text-green-700 dark:text-green-300">
                🎉 {{ promotion.promotion_name }}
              </span>
              <span class="text-xs font-bold text-green-800 dark:text-green-200">
                -${{ promotion.total_discount.toFixed(2) }}
              </span>
            </div>
            <div
              v-if="promotionMessages && promotionMessages.length > 0"
              class="mt-1"
            >
              <div
                v-for="message in promotionMessages"
                :key="message" 
                class="text-xs text-green-600 dark:text-green-400 italic px-2"
              >
                {{ message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="orderType === 'Delivery' && deliveryFee !== undefined && displayedSubtotal > 0"
        class="flex justify-between items-center mb-2"
      >
        <span
          class="text-sm font-medium"
          :class="deliveryFee > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'"
        >
          Costo de envío
          <span
            v-if="deliveryFee === 0"
            class="text-xs ml-1"
          />
        </span>
        <span
          class="text-sm font-medium"
          :class="deliveryFee > 0 ? 'text-orange-900 dark:text-orange-100' : 'text-green-800 dark:text-green-200'"
        >
          +${{ deliveryFee.toFixed(2) }}
        </span>
      </div>

      <div
        v-if="ivaEnabled && ivaAmount && ivaAmount > 0"
        class="flex justify-between items-center mb-2"
      >
        <span class="text-sm font-medium text-blue-600 dark:text-blue-400">
          IVA ({{ (ivaRate || 0.16) * 100 }}%)
        </span>
        <span class="text-sm font-medium text-blue-900 dark:text-blue-100">
          +${{ ivaAmount.toFixed(2) }}
        </span>
      </div>

      <div class="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
        <span class="text-base font-bold text-gray-900 dark:text-gray-100">
          {{ $t('app.views.orders.modals.new_order.total') }}
        </span>
        <span class="text-lg font-bold text-gray-900 dark:text-white">
          ${{ displayedFinalTotal.toFixed(2) }}
        </span>
      </div>
    </div>

    <div
      v-if="showCreateButton"
      class="mt-6"
    >
      <BaseButton
        type="button"
        class="w-full"
        :disabled="isCreateDisabled"
        @click="$emit('create-order')"
      >
        {{ buttonText }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseButton from '@/components/common/BaseButton.vue';
import type { AppliedPromotion } from './orderSummaryTypes';

interface Props {
  orderType?: string;
  displayedSubtotal: number;
  displayedFinalTotal: number;
  currentDiscount?: number;
  finalTotal?: number;
  appliedPromotions?: AppliedPromotion[];
  promotionMessages?: string[];
  isCalculatingPromotions?: boolean;
  deliveryFee?: number;
  ivaEnabled?: boolean;
  ivaRate?: number;
  ivaAmount?: number;
  showCreateButton?: boolean;
  isCreateDisabled?: boolean;
  buttonText?: string;
}

defineProps<Props>();

defineEmits<{
  (e: 'create-order'): void;
}>();

const { t } = useI18n();
</script>
